import { useState, type ReactNode } from 'react';
import type { AtualizarPedidoDto, PedidoVendaResponse, StatusPedido } from '@triade/contracts';
import { useClientes } from '@/api/clientes';
import { useItens } from '@/api/itens';
import { useAtualizarPedido, useCriarPedido, useRepetirPedido } from '@/api/plano';
import { ItemCombobox, type ItemResumo } from '@/components/item-combobox';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useDebounced } from '@/hooks/use-debounced';
import { ApiError } from '@/lib/api';
import { formatarData, formatarMoeda } from '@/lib/format';
import { montarCorpoPedido, pedidoFormDe, pedidoFormVazio, rotuloStatus, STATUS_PEDIDO_ORDEM, type CampoPedido, type PedidoForm } from './pedido-form';

const CAMPO = 'h-[34px] w-full rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');

function Campo({ id, rotulo, erro, children, className }: { id: string; rotulo: string; erro?: string | null; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <label htmlFor={id} className={ROTULO}>
        {rotulo}
      </label>
      {children}
      {erro && (
        <p role="alert" className="mt-1 text-caption text-danger-ink">
          {erro}
        </p>
      )}
    </div>
  );
}

/**
 * Pedido de venda (novo ou existente). Nenhum valor e calculado aqui: depois de salvar, a MC e o MC/t vem da resposta.
 * Erros da API (ex.: SEM_CUSTO) aparecem no formulario.
 */
export function PedidoDialog({ pedido, mes, onFechar }: { pedido: PedidoVendaResponse | null; mes: string; onFechar: () => void }) {
  const criar = useCriarPedido();
  const atualizar = useAtualizarPedido();
  const clientes = useClientes({ limit: 200 });
  const [termo, setTermo] = useState('');
  const termoAtrasado = useDebounced(termo.trim(), 250);
  const itens = useItens({ ativo: true, comerciavel: true, limit: 20, ...(termoAtrasado ? { q: termoAtrasado } : {}) });
  const [form, setForm] = useState<PedidoForm>(() => (pedido ? pedidoFormDe(pedido) : pedidoFormVazio(mes)));
  const [erroCampo, setErroCampo] = useState<{ campo: CampoPedido; mensagem: string } | null>(null);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [salvo, setSalvo] = useState<PedidoVendaResponse | null>(null);
  const salvando = criar.isPending || atualizar.isPending;

  const set = <K extends keyof PedidoForm>(chave: K, valor: PedidoForm[K]) => setForm((f) => ({ ...f, [chave]: valor }));
  const erroDe = (campo: CampoPedido) => (erroCampo?.campo === campo ? erroCampo.mensagem : null);

  // Clientes: a lista vem da API; se o pedido aponta para cliente fora da lista, ele entra como opcao.
  const opcoesCliente = (() => {
    const lista = (clientes.data ?? []).map((c) => ({ id: c.id, nome: c.nome }));
    if (pedido && !lista.some((c) => c.id === pedido.clienteId)) lista.push({ id: pedido.clienteId, nome: pedido.clienteNome });
    return lista;
  })();

  async function gravar() {
    setErroGeral(null);
    setErroCampo(null);
    const montado = montarCorpoPedido(form);
    if (!montado.ok) {
      setErroCampo({ campo: montado.campo, mensagem: montado.mensagem });
      return;
    }
    try {
      const resposta = pedido
        ? await atualizar.mutateAsync({ id: pedido.id, dto: montado.corpo as AtualizarPedidoDto })
        : await criar.mutateAsync(montado.corpo);
      setSalvo(resposta);
    } catch (e) {
      setErroGeral(mensagemApi(e));
      if (e instanceof ApiError && e.code === 'SEM_CUSTO') setErroCampo({ campo: 'itemId', mensagem: 'Este produto não tem custo na data de embarque. Marque "Pedido sem custo" para salvar fora da MC e do resultado.' });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !salvando && onFechar()}>
      <DialogContent className="max-h-[92vh] max-w-[720px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{pedido ? `Pedido nº ${pedido.numero}` : 'Novo pedido'}</DialogTitle>
          <DialogDescription>Quantidade em toneladas (t). Preço por unidade de venda (kg), como na tabela de preços.</DialogDescription>
        </DialogHeader>

        {salvo ? (
          <div role="status" className="flex flex-col gap-3 rounded-xl border border-border bg-surface-muted p-4 text-small">
            <p className="font-semibold">Pedido nº {salvo.numero} salvo.</p>
            {salvo.semCusto ? (
              <p className="text-ink-secondary">Pedido sem custo: fica fora da MC e do resultado do mês.</p>
            ) : salvo.mc !== undefined ? (
              <p>
                Margem de contribuição: <span className="font-semibold tabular-nums">{formatarMoeda(salvo.mc)}</span> · MC por tonelada:{' '}
                <span className="font-semibold tabular-nums">{formatarMoeda(salvo.mcPorUnidade)}</span>
              </p>
            ) : (
              <p className="text-ink-secondary">Sem permissão para ver a margem deste pedido.</p>
            )}
            <DialogFooter>
              <Button onClick={onFechar} className="h-[38px] rounded-[10px] px-4">
                Concluir
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            {erroGeral && (
              <div role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
                Não foi possível salvar: {erroGeral}
              </div>
            )}
            <fieldset disabled={salvando} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Campo id="pedido-cliente" rotulo="Cliente" erro={erroDe('clienteId')}>
                <select id="pedido-cliente" className={CAMPO} value={form.clienteId} aria-invalid={erroDe('clienteId') ? true : undefined} onChange={(e) => set('clienteId', e.target.value)}>
                  <option value="">Escolha o cliente</option>
                  {opcoesCliente.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
                </select>
              </Campo>

              <div>
                <span className={ROTULO}>
                  <label htmlFor="pedido-item">Produto</label>
                </span>
                <ItemCombobox
                  id="pedido-item"
                  valor={form.item}
                  onSelecionar={(it: ItemResumo) => set('item', it)}
                  itens={itens.data ?? []}
                  carregando={itens.isFetching}
                  erro={itens.error?.message ?? null}
                  termo={termo}
                  onTermoChange={setTermo}
                />
                {erroDe('itemId') && (
                  <p role="alert" className="mt-1 text-caption text-danger-ink">
                    {erroDe('itemId')}
                  </p>
                )}
              </div>

              <Campo id="pedido-quantidade" rotulo="Quantidade (t)" erro={erroDe('quantidade')}>
                <input id="pedido-quantidade" inputMode="decimal" className={CAMPO} value={form.quantidade} aria-invalid={erroDe('quantidade') ? true : undefined} onChange={(e) => set('quantidade', e.target.value)} />
              </Campo>

              <Campo id="pedido-preco" rotulo="Preço por unidade (R$)" erro={erroDe('precoUnitario')}>
                <input id="pedido-preco" inputMode="decimal" className={CAMPO} value={form.precoUnitario} aria-invalid={erroDe('precoUnitario') ? true : undefined} onChange={(e) => set('precoUnitario', e.target.value)} />
              </Campo>

              <Campo id="pedido-embarque" rotulo="Data de embarque" erro={erroDe('dataEmbarque')}>
                <input id="pedido-embarque" type="date" className={CAMPO} value={form.dataEmbarque} onChange={(e) => set('dataEmbarque', e.target.value)} />
              </Campo>

              <Campo id="pedido-previsao" rotulo="Previsão de entrega" erro={erroDe('previsaoEntrega')}>
                <input id="pedido-previsao" type="date" className={CAMPO} value={form.previsaoEntrega} onChange={(e) => set('previsaoEntrega', e.target.value)} />
              </Campo>

              <Campo id="pedido-status" rotulo="Status">
                <select id="pedido-status" className={CAMPO} value={form.status} onChange={(e) => set('status', e.target.value as StatusPedido)}>
                  {STATUS_PEDIDO_ORDEM.map((s) => (
                    <option key={s} value={s}>
                      {rotuloStatus(s)}
                    </option>
                  ))}
                </select>
              </Campo>

              <Campo id="pedido-modalidade" rotulo="Modalidade de frete">
                <select id="pedido-modalidade" className={CAMPO} value={form.modalidadeFrete} onChange={(e) => set('modalidadeFrete', e.target.value as PedidoForm['modalidadeFrete'])}>
                  <option value="">Não informada</option>
                  <option value="CIF">CIF (vendedor paga o frete)</option>
                  <option value="FOB">FOB (cliente paga o frete)</option>
                </select>
              </Campo>

              <Campo id="pedido-transportadora" rotulo="Transportadora">
                <input id="pedido-transportadora" className={CAMPO} value={form.transportadora} onChange={(e) => set('transportadora', e.target.value)} />
              </Campo>

              <fieldset className="sm:col-span-2">
                <legend className={ROTULO}>Frete</legend>
                <div className="flex flex-wrap items-center gap-4 text-small text-ink-body">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="pedido-tipo-frete" checked={form.tipoFrete === 'total'} onChange={() => set('tipoFrete', 'total')} />
                    Frete total
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="pedido-tipo-frete" checked={form.tipoFrete === 'porUnidade'} onChange={() => set('tipoFrete', 'porUnidade')} />
                    Frete por tonelada
                  </label>
                  <input
                    id="pedido-frete"
                    aria-label={form.tipoFrete === 'total' ? 'Frete total (R$)' : 'Frete por tonelada (R$)'}
                    inputMode="decimal"
                    className={`${CAMPO} max-w-[200px]`}
                    value={form.frete}
                    onChange={(e) => set('frete', e.target.value)}
                  />
                </div>
                {erroDe('frete') && (
                  <p role="alert" className="mt-1 text-caption text-danger-ink">
                    {erroDe('frete')}
                  </p>
                )}
              </fieldset>

              <Campo id="pedido-comissao" rotulo="Comissão (%)" erro={erroDe('comissao')}>
                <input id="pedido-comissao" inputMode="decimal" className={CAMPO} value={form.comissao} onChange={(e) => set('comissao', e.target.value)} />
              </Campo>

              <Campo id="pedido-vendedor" rotulo="Vendedor">
                <input id="pedido-vendedor" className={CAMPO} value={form.vendedorNome} onChange={(e) => set('vendedorNome', e.target.value)} />
              </Campo>

              <Campo id="pedido-numero-cliente" rotulo="Nº do pedido do cliente">
                <input id="pedido-numero-cliente" className={CAMPO} value={form.numeroPedidoCliente} onChange={(e) => set('numeroPedidoCliente', e.target.value)} />
              </Campo>

              <Campo id="pedido-observacao" rotulo="Observação" className="sm:col-span-2">
                <textarea id="pedido-observacao" rows={2} className="w-full rounded-[9px] border border-input bg-card px-2.5 py-2 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none" value={form.observacao} onChange={(e) => set('observacao', e.target.value)} />
              </Campo>

              <label className="flex items-center gap-2 text-small text-ink-body sm:col-span-2">
                <input type="checkbox" id="pedido-sem-custo" checked={form.semCusto} onChange={(e) => set('semCusto', e.target.checked)} />
                Pedido sem custo (fora da MC e do resultado)
              </label>
            </fieldset>

            <DialogFooter>
              <Button variant="outline" onClick={onFechar} disabled={salvando} className="h-[38px] rounded-[10px] px-4">
                Cancelar
              </Button>
              <Button onClick={() => void gravar()} disabled={salvando} className="h-[38px] rounded-[10px] px-4">
                {salvando ? 'Salvando…' : 'Salvar pedido'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Repeticao do pedido nos meses seguintes (POST /carteira/:id/repetir). */
export function RepetirPedidoDialog({ pedido, onFechar }: { pedido: PedidoVendaResponse; onFechar: () => void }) {
  const repetir = useRepetirPedido();
  const [meses, setMeses] = useState('1');
  const [criados, setCriados] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const numero = Number(meses);
  const valido = Number.isInteger(numero) && numero >= 1 && numero <= 24;

  async function confirmar() {
    setErro(null);
    try {
      const lista = await repetir.mutateAsync({ id: pedido.id, dto: { meses: numero } });
      setCriados(lista.length);
    } catch (e) {
      setErro(mensagemApi(e));
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !repetir.isPending && onFechar()}>
      <DialogContent className="max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Repetir pedido nº {pedido.numero}</DialogTitle>
          <DialogDescription>Cria o mesmo pedido nos meses seguintes ao embarque ({formatarData(pedido.dataEmbarque)}).</DialogDescription>
        </DialogHeader>
        {criados !== null ? (
          <div role="status" className="flex flex-col gap-3 text-small">
            <p>{criados === 1 ? '1 pedido criado.' : `${criados} pedidos criados.`}</p>
            <DialogFooter>
              <Button onClick={onFechar} className="h-[38px] rounded-[10px] px-4">
                Concluir
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            {erro && (
              <div role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
                Não foi possível repetir: {erro}
              </div>
            )}
            <Campo id="repetir-meses" rotulo="Quantos meses seguintes (1 a 24)" erro={!valido ? 'Informe de 1 a 24 meses' : null}>
              <input id="repetir-meses" type="number" min={1} max={24} className={CAMPO} value={meses} aria-invalid={!valido ? true : undefined} onChange={(e) => setMeses(e.target.value)} />
            </Campo>
            <DialogFooter>
              <Button variant="outline" onClick={onFechar} disabled={repetir.isPending} className="h-[38px] rounded-[10px] px-4">
                Cancelar
              </Button>
              <Button onClick={() => void confirmar()} disabled={!valido || repetir.isPending} className="h-[38px] rounded-[10px] px-4">
                {repetir.isPending ? 'Repetindo…' : 'Repetir'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
